import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den "Automatische Schichtplanung"-Dialog.
 * Wird via Hilfe-Button (`<app-dialog-help-button>`) im Hero-Header manuell
 * gestartet. Erklaert in 7 Schritten: Welcome → Zeitraum → Strategien
 * (Uebersicht) → Empfohlene Strategie → Erweiterte Einstellungen (Accordion
 * wird programmatisch geoeffnet) → Simulation starten → Diagnose-Tab (nur
 * sichtbar, wenn schon eine Simulation gelaufen ist).
 *
 * Pattern analog zu `TERMINAL_ADD_CONTEXTUAL_TOUR` / `EMPLOYEE_ADD_CONTEXTUAL_TOUR`,
 * aber erklaerungs- statt formular-getrieben (manuelles Weiterklicken, keine
 * `nextCondition: 'onInput'`).
 */

/**
 * Oeffnet das "Erweiterte Einstellungen"-Accordion im Inputs-Tab via
 * synthetischem Klick auf den Toggle-Button. Idempotent: prueft
 * `aria-expanded`, klickt nur wenn noch geschlossen.
 */
function openAdvancedAccordion(): void {
  const toggle = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="${AnchorId.AutoSuggestAdvanced}"] .it-card__toggle`
  );
  if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
    toggle.click();
  }
}

/** Schliesst das Accordion wieder (Cleanup nach Step-Verlassen). */
function closeAdvancedAccordion(): void {
  const toggle = document.querySelector<HTMLButtonElement>(
    `[onboardingAnchor="${AnchorId.AutoSuggestAdvanced}"] .it-card__toggle`
  );
  if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
    toggle.click();
  }
}

export const AUTO_SUGGEST_HELP_TOUR: TourConfig = {
  id: TourFeature.AutoSuggestHelp,
  version: 1,
  title: 'AUTO_SUGGEST_HELP_TOUR_TITLE',
  description: 'AUTO_SUGGEST_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren, dann den
  // "Auto-Vorschlag"-Toolbar-Button klicken.
  launchContext: {
    route: '/planner',
    opens: { openerAnchor: AnchorId.ShiftActionAutofill, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 90,
  // Bewusst NICHT 'contextual-help': diese Kategorie aktiviert in
  // TourCoordinator.startTour den HelpModeService — also kleine (i)-Icons pro
  // Anchor mit on-demand Popover (Atlassian/Stripe-Pattern). Fuer Auto-Suggest
  // wollen wir die klassische Guided Tour mit Spotlight + Voice-Over (Pendo/
  // Appcues-Pattern). 'feature-discovery' faellt im Coordinator in den normalen
  // V2-Renderer-Pfad durch und triggert den Audio-Service.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'AUTO_SUGGEST_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog, nicht nur den Header — Spotlight
          // muss den kompletten Dialog-Container umschliessen. Anchor am
          // .as-Wrapper, nicht am .as-hero.
          anchor: AnchorId.AutoSuggestDialog,
          title: 'TUTORIAL.AUTO_SUGGEST.WELCOME.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'period',
          anchor: AnchorId.AutoSuggestPeriod,
          title: 'TUTORIAL.AUTO_SUGGEST.PERIOD.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.PERIOD.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'strategy-overview',
          anchor: AnchorId.AutoSuggestStrategyGrid,
          title: 'TUTORIAL.AUTO_SUGGEST.STRATEGY.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.STRATEGY.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'strategy-recommended',
          anchor: AnchorId.AutoSuggestStrategyBalanced,
          title: 'TUTORIAL.AUTO_SUGGEST.RECOMMENDED.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.RECOMMENDED.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'advanced',
          anchor: AnchorId.AutoSuggestAdvanced,
          title: 'TUTORIAL.AUTO_SUGGEST.ADVANCED.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.ADVANCED.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          onBeforeShow: openAdvancedAccordion,
          onAfterHide: closeAdvancedAccordion,
        },
        {
          id: 'run',
          anchor: AnchorId.AutoSuggestRunButton,
          title: 'TUTORIAL.AUTO_SUGGEST.RUN.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.RUN.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // "Weiter" loest hier die Simulation aus, statt direkt
          // weiterzuspringen: `nextClicksAnchor` feuert einen synthetischen
          // Klick auf den "Simulation starten"-Button (= `anchor`). Die Tour
          // bleibt auf diesem Step stehen, bis die Berechnung fertig ist;
          // danach dispatcht auto-suggest.component.ts:378 das Event
          // 'onboarding:auto-suggest.outcome-ready', und die Tour rueckt
          // ueber `advanceOnDocumentEvent` automatisch zu Step 7 (Diagnose)
          // vor. So fuehren BEIDE Wege — Klick auf "Weiter" wie auf
          // "Simulation starten" — zum selben Ergebnis, und Step 7 erscheint
          // garantiert erst NACH einer gelaufenen Simulation.
          nextClicksAnchor: true,
          advanceOnDocumentEvent: 'onboarding:auto-suggest.outcome-ready',
        },
        {
          id: 'diagnosis',
          // Popover sitzt am Diagnose-Tab links (kompakter Ankerpunkt), aber der
          // Spotlight umfasst den GANZEN Dialog — die Diagnose-Erklaerung
          // bezieht sich auf das gesamte Ergebnis-Cockpit (Heatmap, KPI-Strip,
          // Hindernis-Liste rechts), nicht nur den Tab-Button links.
          anchor: AnchorId.AutoSuggestDiagnosisTab,
          spotlightAnchor: AnchorId.AutoSuggestDialog,
          title: 'TUTORIAL.AUTO_SUGGEST.DIAGNOSIS.TITLE',
          content: 'TUTORIAL.AUTO_SUGGEST.DIAGNOSIS.CONTENT',
          placement: 'right',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step der Tour: kein Zurueck-Button, nur "Abschliessen".
          // Sonst koennte der User zurueck zu Step 6 navigieren und die
          // Diagnose erneut triggern, was die UX verwirrend macht.
          //
          // Kein `skipIf` noetig: Step 6 nutzt `nextClicksAnchor`, daher ist
          // der einzige Vorwaerts-Pfad das 'outcome-ready'-Event — bei
          // Erreichen von Step 7 existiert also garantiert ein Outcome. Ein
          // skipIf auf die `.pending`-Klasse wuerde hier sogar fehlzuenden:
          // das Event feuert synchron direkt nach `this.outcome = …`, bevor
          // Angular die Klasse entfernt → Step 7 koennte faelschlich
          // uebersprungen werden.
          showPrevButton: false,
        },
      ],
    },
  ],
};
